/**
 * Cloudflare Turnstile.
 *
 * Guards the three endpoints the backend's `TurnstileMiddleware` protects:
 * signup, password-reset request, and password-reset completion. The token
 * travels in `X-Turnstile-Token`; the secret that validates it never leaves the
 * server, and the site key here is public configuration rather than a secret.
 *
 * Off unless `VITE_TURNSTILE_SITE_KEY` is set, which mirrors the backend's
 * `TURNSTILE_REQUIRED=false` default. A widget that renders with no key would be
 * a permanently failing challenge in local development — visibly broken, and
 * blocking every signup.
 *
 * A token is single-use: Cloudflare rejects the second submission of the same
 * one. `reset()` therefore runs after every failed attempt, which is the part
 * that is easy to leave out and produces a form that works exactly once.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const SITE_KEY: string = import.meta.env?.VITE_TURNSTILE_SITE_KEY ?? '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const turnstileEnabled = Boolean(SITE_KEY);

interface TurnstileApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof document === 'undefined') return Promise.reject(new Error('no document'));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Turnstile could not load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface TurnstileHandle {
  /** The current token, or null while unsolved. */
  token: string | null;
  /** Mount point. Render inside the form. */
  Widget: () => JSX.Element | null;
  /** Discard a spent token and re-challenge. Call after every failed submit. */
  reset: () => void;
  /** True when a challenge is configured and has not yet been solved. */
  blocking: boolean;
}

export function useTurnstile(): TurnstileHandle {
  const [token, setToken] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  const mount = useCallback((element: HTMLDivElement | null) => {
    containerRef.current = element;
    if (!element || !turnstileEnabled || widgetIdRef.current) return;

    void loadScript()
      .then(() => {
        if (!containerRef.current || !window.turnstile || widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          // The managed widget is the accessible one; Cloudflare handles the
          // keyboard and screen-reader path for the interactive challenge.
          theme: 'dark',
          callback: (value: string) => setToken(value),
          'error-callback': () => {
            setToken(null);
            setFailed(true);
          },
          'expired-callback': () => setToken(null),
        });
      })
      .catch(() => setFailed(true));
  }, []);

  useEffect(
    () => () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* the widget was already torn down with its container */
        }
      }
      widgetIdRef.current = null;
    },
    [],
  );

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current);
  }, []);

  const Widget = useCallback(() => {
    if (!turnstileEnabled) return null;
    return (
      <div className="mm-turnstile">
        <div ref={mount} />
        {failed ? (
          <p className="mm-turnstile__note" role="status">
            The challenge could not load. Check your connection and reload the page.
          </p>
        ) : null}
      </div>
    );
  }, [mount, failed]);

  return { token, Widget, reset, blocking: turnstileEnabled && token === null };
}
